import styles from './ProjectTable.module.scss';

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@work4all/data';

import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ITableSchema } from '@work4all/models/lib/table-schema/table-schema';

import { useLeftAreaVisibleFilters } from '../../../hooks/use-left-area-visible-filters';
import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import { GridStateProvider } from '../grid/GridStateProvider';
import jsonSchema from '../schemata/project-table-schema.json';
import { TableAreas } from '../table/EntityTableLayoutV8';
import { useSyncScroll } from '../table/hooks/use-sync-scroll';

import { ProjectBanderoleInfo } from './components/ProjectBanderoleInfo';
import { ProjectRightsGrid } from './components/ProjectRightsGrid';
import { ProjectTableQuickFilters } from './components/ProjectTableQuickFilters';
import { useProjectActions } from './hooks/use-project-actions';
import { useProjectGrid } from './hooks/use-project-grid';
import { useRightsSchema } from './hooks/use-rights.schema';

type IProjectTableCustomColumns = Record<string, never>;

const defaultSort = [
  { field: 'startDateOuter', direction: SortDirection.DESCENDING },
];

const secondarySort = [
  { field: 'number', direction: SortDirection.DESCENDING },
];

export function ProjectRightsTableGrid(props: IEntityTableV8) {
  return (
    <GridStateProvider>
      <ProjectRightsTableGridInner {...props} />
    </GridStateProvider>
  );
}

function ProjectRightsTableGridInner(props: IEntityTableV8) {
  const user = useUser();
  const schema = jsonSchema as unknown as ITableSchema<never>;

  const matrixSchema = useRightsSchema(schema);

  const forceRequestFields: Project = useMemo<Project>(() => {
    if (!user.isMaster) return props.forceRequestFields as Project;
    return {
      ...props.forceRequestFields,
      projectAccessGroups: [
        {
          id: null,
        },
      ],
    } as Project;
  }, [props.forceRequestFields, user.isMaster]);

  const dataTable = useEntityDataTableV8<Project, IProjectTableCustomColumns>({
    ...props,
    schema: matrixSchema,
    defaultSort,
    secondarySort,
    forceRequestFields,
    rowModifiers: (value) => {
      return {
        isFaded: value.projectStatus?.closedStatus === -1,
      };
    },
  });

  const listScrollRef = useRef<HTMLDivElement>(null);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  useSyncScroll('vertical', listScrollRef, gridScrollRef);

  const matrix = useProjectGrid({
    table: dataTable.table,
    projects: dataTable.data as Project[],
    refetch: dataTable.refetch,
    columnConfigs: dataTable.columnConfigs,
  });
  const projectActions = useProjectActions(matrix);

  const classes = useMemo(() => {
    return {
      headerWrapper: styles.headerRow,
    };
  }, []);

  const actions = useMemo(() => {
    return {
      custom: {
        left: projectActions,
        hideDivider: true,
      },
    };
  }, [projectActions]);

  const { t } = useTranslation();
  const { action: leftAreaVisibleFiltersAction } = useLeftAreaVisibleFilters({
    entity: Entities.project,
    filters: [
      { id: 'personal', label: t('COMMON.PERSONAL_FILTERS') },
      { id: 'general', label: t('COMMON.GENERAL_FILTERS') },
    ],
  });

  const selectedRows = dataTable.table.getSelectedRowModel().rows;

  const areas = useMemo(() => {
    const _areas: TableAreas = {
      left: {
        title: t('COMMON.FILTER'),
        content: <ProjectTableQuickFilters table={dataTable.table} />,
        resizable: true,
        actions: [leftAreaVisibleFiltersAction],
      },
      center: {
        content: (
          <ProjectRightsGrid
            scrollRef={gridScrollRef}
            {...matrix}
            selectedRows={selectedRows}
          />
        ),
      },
      right: undefined,
    };

    return _areas;
  }, [t, dataTable.table, leftAreaVisibleFiltersAction, matrix, selectedRows]);

  const overrides = useMemo(() => {
    return {
      actions,
      areas,
    };
  }, [actions, areas]);

  return (
    <div className={styles.root}>
      <ProjectBanderoleInfo />
      <EntityTableV8
        {...props}
        {...dataTable}
        layout="table"
        areas={areas}
        actions={actions}
        scrollRef={listScrollRef}
        rowHeightRem="3rem"
        basicClasses={classes}
        overrides={overrides}
      />
    </div>
  );
}
